// Libraries
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {DrawerType, useDrawer, useResponsive} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';

// App
import Button from '@shared/design/components/Button';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import CreateProjectClaimDrawer from '@shared/modules/Project/components/Claim/CreateProjectClaimDrawer';
import ProjectClaimsV2 from '@shared/modules/Project/components/Claim/ProjectClaimsV2';
import ProjectDetailsBlockHeader from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';

const BLOCK_TITLE = 'Claims';

const Row = Styled.View`
  flex-direction: row;
`;

const Column = Styled.View`
`;

const SkeletonRow = () => {
  return (
    <Row>
      <Column style={{flex: 2}}>
        <SkeletonLoader width={50} height={SkeletonLoader.HEIGHT.Text} />
      </Column>
      <Column style={{flex: 2}}>
        <SkeletonLoader width={40} height={SkeletonLoader.HEIGHT.Text} />
      </Column>
      <Column style={{flex: 2}}>
        <SkeletonLoader width={50} height={SkeletonLoader.HEIGHT.Text} />
      </Column>
      <Column style={{flex: 2}}>
        <SkeletonLoader width={75} height={SkeletonLoader.HEIGHT.Text} />
      </Column>
      <Column style={{flex: 3}}>
        <SkeletonLoader width={75} height={SkeletonLoader.HEIGHT.Text} />
      </Column>
    </Row>
  );
};

const SkeletonBody = () => {
  return (
    <Column>
      <SkeletonRow />
      <Space height={16} />
      <SkeletonRow />
      <Space height={16} />
      <SkeletonRow />
    </Column>
  );
};

const SkeletonComponent = () => {
  return (
    <ActionPanel
      BodyComponent={SkeletonBody}
      ActionButtonComponent={() => (
        <SkeletonLoader width={128} height={SkeletonLoader.HEIGHT.ButtonSmall} />
      )}
      title={BLOCK_TITLE}
      style={{width: '100%'}}
    />
  );
};

const ClaimActions = ({createClaimDrawer}: {createClaimDrawer: DrawerType}) => {
  return (
    <Button
      iconLeft={Icon.Plus}
      text={'Create Claim'}
      isSmall
      onPress={createClaimDrawer.handleOpen}
    />
  );
};

const CreateButton = ({createClaimDrawer}: {createClaimDrawer: DrawerType}) => {
  return (
    <TertiaryButton
      text={`Add`}
      iconLeft={Icon.Plus}
      onPress={createClaimDrawer.handleOpen}
      isResponsive
    />
  );
};

const ProjectDetailsClaimsBlockContent = ({
  project,
  refetch,
  createClaimDrawer,
  isWidget,
}: {
  project: ProjectModel;
  refetch: () => void;
  createClaimDrawer: DrawerType;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      {responsive.desktop && !isWidget ? (
        <ActionPanel
          BodyComponent={ProjectClaimsV2}
          bodyComponentProps={{project, refetch}}
          ActionButtonComponent={ClaimActions}
          actionButtonComponentProps={{createClaimDrawer}}
          title={BLOCK_TITLE}
          style={{width: '100%'}}
          bodyStyle={{padding: 0}}
        />
      ) : (
        <ProjectDetailsClaimsBlockContentWidget
          key={project.claimAttachmentsHash}
          project={project}
          createClaimDrawer={createClaimDrawer}
          refetch={refetch}
          isWidget={isWidget}
        />
      )}
    </React.Fragment>
  );
};

const ProjectDetailsClaimsBlockContentWidget = ({
  project,
  createClaimDrawer,
  refetch,
  isWidget,
}: {
  project: ProjectModel;
  createClaimDrawer: DrawerType;
  refetch: () => void;
  isWidget?: boolean;
}) => {
  return (
    <React.Fragment>
      <ProjectDetailsBlockHeader
        title={BLOCK_TITLE}
        ActionsComponent={CreateButton}
        actionsComponentProps={{createClaimDrawer}}
        dividerHeight={8}
        style={{paddingTop: 16, paddingLeft: 16, paddingRight: 16}}
      />
      <ProjectClaimsV2 project={project} refetch={refetch} isWidget={isWidget} />
    </React.Fragment>
  );
};

const ProjectDetailsClaimsBlock = ({
  projectUuid,
  project,
  index,
  isWidget = true,
}: {
  projectUuid: string;
  project?: ProjectModel;
  index: number;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();
  const createClaimDrawer = useDrawer({name: 'Create Claim Drawer'});

  return (
    <ProjectDetailsBlockWrapper
      key={`${projectUuid}_${project?.claimAttachmentsHash}`}
      index={index}
      query={ProjectDetailsClaimsBlock.query}
      queryVariables={{
        projectUuid,
        isDesktop: responsive.desktop && !isWidget,
      }}
      SkeletonComponent={SkeletonComponent}
      style={{padding: 0}}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        return (
          <React.Fragment>
            <ProjectDetailsClaimsBlockContent
              project={data.project}
              refetch={refetch}
              createClaimDrawer={createClaimDrawer}
              isWidget={isWidget}
            />
            <CreateProjectClaimDrawer
              key={createClaimDrawer.key}
              isOpen={createClaimDrawer.isOpen}
              handleClose={createClaimDrawer.handleClose}
              project={data.project}
              onSuccess={() => {
                createClaimDrawer.handleClose();
                refetch();
              }}
            />
          </React.Fragment>
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsClaimsBlock.fragment = gql`
  fragment ProjectDetailsClaimsBlock on Project {
    id
    claimAttachmentsHash
  }
`;

ProjectDetailsClaimsBlock.query = gql`
  ${CreateProjectClaimDrawer.fragment}
  ${ProjectClaimsV2.fragment}

  query ProjectDetailsClaimsBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      ...CreateProjectClaimDrawer
      ...ProjectClaimsV2
    }
  }
`;

export default ProjectDetailsClaimsBlock;
